import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FiMapPin, FiBriefcase, FiClock, FiSend, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import careersData from '../data/careers.json';

const perks = [
  { icon: '💰', title: 'Competitive Salary', desc: 'Above-industry compensation with annual increments and performance bonuses' },
  { icon: '📈', title: 'Career Growth', desc: 'Clear career progression paths with leadership opportunities as we scale' },
  { icon: '🎓', title: 'Learning & Development', desc: 'Regular training, certifications, and skill development programs' },
  { icon: '🤝', title: 'Great Team Culture', desc: 'Work with a passionate, driven team that celebrates success together' },
  { icon: '🏥', title: 'Health Benefits', desc: 'Medical insurance coverage for you and your family' },
  { icon: '🏡', title: 'Work-Life Balance', desc: 'Flexible working arrangements and supportive management' },
];

const initialForm = { name: '', email: '', mobile: '', position: '', experience: '', message: '' };

export default function Careers() {
  const [expanded, setExpanded] = useState(null);
  const [applyFor, setApplyFor] = useState('');
  const [form, setForm] = useState(initialForm);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleApply = (job) => {
    setApplyFor(job.title);
    setForm(f => ({ ...f, position: job.title }));
    document.getElementById('apply-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitting(true);
    setTimeout(() => { setSubmitting(false); setSubmitted(true); }, 1500);
  };

  const tagColors = { sales: 'var(--primary)', ops: 'var(--accent-dark)', fulltime: 'var(--secondary-dark)' };
  const tagBg = { sales: 'var(--primary-xlight)', ops: 'var(--accent-light)', fulltime: 'var(--secondary-light)' };

  return (
    <>
      <Helmet>
        <title>Careers | Avivan Enterprises — Join Our FMCG Distribution Team</title>
        <meta name="description" content="Explore career opportunities at Avivan Enterprises. Join our growing FMCG distribution team and build a rewarding career in supply chain and sales." />
      </Helmet>

      {/* Page Banner */}
      <div className="page-banner">
        <div className="container">
          <div className="page-banner-content text-center">
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(243,156,18,0.2)', color: '#f39c12', padding: '6px 18px', borderRadius: 50, fontSize: '0.78rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: 16, border: '1px solid rgba(243,156,18,0.3)' }}>
              💼 Join Our Team
            </div>
            <h1>Careers at Avivan Enterprises</h1>
            <p>Build your career with one of India's fastest growing FMCG distribution companies</p>
            <div className="breadcrumb-c" style={{ justifyContent: 'center' }}>
              <Link to="/">Home</Link>
              <span className="sep">/</span>
              <span style={{ color: 'var(--accent)' }}>Careers</span>
            </div>
          </div>
        </div>
      </div>

      {/* Why Work With Us */}
      <section style={{ padding: '90px 0' }}>
        <div className="container">
          <div className="section-header" data-aos="fade-up">
            <span className="section-badge">⭐ Why Join Us</span>
            <h2 className="section-title">Why Work With <span>Avivan Enterprises?</span></h2>
            <p className="section-subtitle">We're building a culture where talented people thrive and careers grow alongside the business</p>
            <div className="divider-bar" />
          </div>
          <div className="row g-4">
            {perks.map((p, i) => (
              <div className="col-md-4 col-sm-6" key={p.title} data-aos="fade-up" data-aos-delay={i * 70}>
                <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 16, padding: '28px 24px', textAlign: 'center', height: '100%', transition: 'var(--transition)' }} className="card-hover">
                  <div style={{ fontSize: '2.5rem', marginBottom: 14 }}>{p.icon}</div>
                  <h4 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: 8 }}>{p.title}</h4>
                  <p style={{ fontSize: '0.84rem', color: 'var(--text-muted)', margin: 0, lineHeight: 1.6 }}>{p.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section style={{ padding: '80px 0', background: 'var(--light)' }}>
        <div className="container">
          <div className="section-header" data-aos="fade-up">
            <span className="section-badge">📋 Openings</span>
            <h2 className="section-title">Current <span>Openings</span></h2>
            <p className="section-subtitle">Join our team and be part of India's FMCG distribution growth story</p>
            <div className="divider-bar" />
          </div>
          <div className="row g-4">
            {careersData.map((job, i) => (
              <div className="col-12" key={job.id} data-aos="fade-up" data-aos-delay={i * 60}>
                <div className="job-card">
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
                        <h3 style={{ fontSize: '1.05rem', fontWeight: 800, margin: 0 }}>{job.title}</h3>
                        <span style={{ background: tagBg[job.tag], color: tagColors[job.tag], padding: '3px 12px', borderRadius: 20, fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase' }}>
                          {job.type}
                        </span>
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, color: 'var(--text-muted)', fontSize: '0.83rem' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}><FiBriefcase size={13} /> {job.department}</span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}><FiMapPin size={13} /> {job.location}</span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}><FiClock size={13} /> {job.experience}</span>
                      </div>
                      {expanded === job.id && (
                        <div style={{ marginTop: 16 }}>
                          <p style={{ fontSize: '0.9rem', color: 'var(--dark)', lineHeight: 1.7, marginBottom: 12 }}>{job.description}</p>
                          <div style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--dark)', marginBottom: 8 }}>Requirements:</div>
                          <ul style={{ paddingLeft: 20, margin: 0 }}>
                            {job.requirements.map(r => (
                              <li key={r} style={{ fontSize: '0.84rem', color: 'var(--text-muted)', marginBottom: 4, lineHeight: 1.5 }}>{r}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', flexShrink: 0 }}>
                      <button
                        onClick={() => setExpanded(expanded === job.id ? null : job.id)}
                        style={{ background: 'var(--light)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.82rem', fontWeight: 600, color: 'var(--dark)', transition: 'var(--transition)' }}
                      >
                        {expanded === job.id ? <FiChevronUp size={14} /> : <FiChevronDown size={14} />}
                        {expanded === job.id ? 'Less' : 'Details'}
                      </button>
                      <button
                        onClick={() => handleApply(job)}
                        className="btn-primary-c"
                        style={{ fontSize: '0.85rem', padding: '8px 20px' }}
                      >
                        Apply Now
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* General Application */}
          <div style={{ marginTop: 40, background: 'var(--white)', border: '2px dashed var(--border)', borderRadius: 16, padding: '32px', textAlign: 'center' }} data-aos="fade-up">
            <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>💼</div>
            <h3 style={{ fontSize: '1.1rem', fontWeight: 800, marginBottom: 8 }}>Don't See Your Role?</h3>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', maxWidth: 400, margin: '0 auto 20px' }}>
              We're always looking for talented professionals. Send us your profile and we'll reach out when the right opportunity opens.
            </p>
            <button
              onClick={() => { setApplyFor('General Application'); document.getElementById('apply-form')?.scrollIntoView({ behavior: 'smooth' }); }}
              className="btn-outline-c"
            >
              Send Open Application
            </button>
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section id="apply-form" style={{ padding: '90px 0' }}>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-8">
              <div className="section-header" data-aos="fade-up">
                <span className="section-badge"><FiSend size={12} /> Apply Now</span>
                <h2 className="section-title">
                  {applyFor ? `Apply for: ${applyFor}` : 'Submit Your <span>Application</span>'}
                </h2>
                <div className="divider-bar" />
              </div>

              {submitted ? (
                <div data-aos="fade-up" style={{ background: '#d4edda', border: '1px solid #28A745', borderRadius: 16, padding: '48px 40px', textAlign: 'center' }}>
                  <div style={{ fontSize: '4rem', marginBottom: 16 }}>🎉</div>
                  <h3 style={{ color: '#1e7e34', fontWeight: 800, marginBottom: 12 }}>Application Received!</h3>
                  <p style={{ color: '#155724', lineHeight: 1.7 }}>
                    Thank you for your interest in joining Avivan Enterprises. Our HR team will review your profile and get in touch within <strong>3-5 business days</strong>.
                  </p>
                  <button className="btn-primary-c" style={{ marginTop: 20 }} onClick={() => { setForm(initialForm); setSubmitted(false); setApplyFor(''); }}>
                    Submit Another Application
                  </button>
                </div>
              ) : (
                <div data-aos="fade-up" style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 20, padding: '40px', boxShadow: 'var(--shadow-sm)' }}>
                  <form onSubmit={handleSubmit}>
                    <div className="row">
                      <div className="col-md-6">
                        <div className="form-group-c">
                          <label className="form-label-c">Full Name <span style={{ color: 'red' }}>*</span></label>
                          <input className="form-control-c" name="name" placeholder="Your full name" value={form.name} onChange={handleChange} required />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="form-group-c">
                          <label className="form-label-c">Email Address <span style={{ color: 'red' }}>*</span></label>
                          <input className="form-control-c" name="email" type="email" placeholder="your@email.com" value={form.email} onChange={handleChange} required />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="form-group-c">
                          <label className="form-label-c">Mobile Number <span style={{ color: 'red' }}>*</span></label>
                          <input className="form-control-c" name="mobile" placeholder="10-digit mobile number" value={form.mobile} onChange={handleChange} required />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="form-group-c">
                          <label className="form-label-c">Position Applying For</label>
                          <select className="form-select-c" name="position" value={form.position} onChange={handleChange}>
                            <option value="">Select Position</option>
                            {careersData.map(j => <option key={j.id} value={j.title}>{j.title}</option>)}
                            <option value="General Application">General Application</option>
                          </select>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="form-group-c">
                          <label className="form-label-c">Years of Experience</label>
                          <select className="form-select-c" name="experience" value={form.experience} onChange={handleChange}>
                            <option value="">Select experience</option>
                            {['Fresher', '1-2 Years', '3-5 Years', '5-8 Years', '8+ Years'].map(e => <option key={e}>{e}</option>)}
                          </select>
                        </div>
                      </div>
                      <div className="col-12">
                        <div className="form-group-c">
                          <label className="form-label-c">Cover Letter / Message</label>
                          <textarea className="form-control-c" name="message" rows="5" placeholder="Tell us about yourself, your experience in FMCG/distribution industry, why you want to join Avivan Enterprises, and what value you can add to our team..." value={form.message} onChange={handleChange} />
                        </div>
                      </div>
                    </div>
                    <button type="submit" className="btn-primary-c w-100" style={{ fontSize: '1rem', padding: 15, justifyContent: 'center' }} disabled={submitting}>
                      {submitting ? <><span className="spinner-border spinner-border-sm me-2" /> Submitting...</> : <><FiSend size={16} /> Submit Application</>}
                    </button>
                  </form>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
